import type { ReactNode } from "react";
import type { ProjectBriefExtension, ProjectBriefProperty } from "@/types";
import { TextField, SelectField, Button } from "@/components/ui";
import {
  ENUMS,
  EAVES_BANDS,
  HEIGHT_BANDS,
  MIXED_STOREY_TYPE,
  WRAP_POSITION,
  emptyExtension,
  prettyLabel,
  derivedStoreys,
  storeysAutoDerived,
} from "@/lib/feasibility";

/** "" → undefined, else a finite number (or undefined if non-numeric). */
function numOrUndef(value: string): number | undefined {
  if (value.trim() === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function EnumSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value?: string;
  options: readonly string[];
  onChange: (v: string) => void;
}) {
  return (
    <SelectField label={label} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
      <option value="">—</option>
      {options.map((o) => (
        <option key={o} value={o}>{prettyLabel(o)}</option>
      ))}
    </SelectField>
  );
}

/** One extension line's fields (type/position/storeys/footprint/heights). Storeys
 *  auto-derives (and hides) for single/two-storey. */
function ExtensionLineFields({
  index,
  line,
  removable,
  onChange,
  onRemove,
}: {
  index: number;
  line: ProjectBriefExtension;
  removable: boolean;
  onChange: (patch: Partial<ProjectBriefExtension>) => void;
  onRemove: () => void;
}): ReactNode {
  const autoStoreys = storeysAutoDerived(line.extension_type);
  const isWrap = line.position === WRAP_POSITION;
  const isMixed = line.extension_type === MIXED_STOREY_TYPE;
  // A mixed-storey line's depth/width become the GROUND FLOOR (the upper floor gets its own
  // pair below); a wrap's are the rear leg. Wrap wins the label when both apply — the corner
  // convention is the one that has bitten.
  const depthLabel = isWrap ? "Rear leg — depth (m)" : isMixed ? "Ground floor — depth (m)" : "Depth (m)";
  const depthHint = isWrap ? "Projection beyond the rear wall" : isMixed ? "Projection beyond the existing wall" : undefined;
  const widthLabel = isWrap ? "Rear leg — width (m)" : isMixed ? "Ground floor — width (m)" : "Width (m)";
  const widthHint = isWrap ? "Full width of the back, including the corner" : undefined;
  const heightHint = HEIGHT_BANDS.find((b) => b.value === Number(line.height_m))?.hint;
  const eavesHint = EAVES_BANDS.find((b) => b.value === Number(line.eaves_height_m))?.hint;
  return (
    <div className="brief-ext">
      <div className="brief-ext__head">
        <span className="brief-ext__title">Extension {index + 1}</span>
        {removable ? (
          <button type="button" className="brief-ext__remove" onClick={onRemove}>Remove</button>
        ) : null}
      </div>
      <div className="lead-drawer__two">
        <EnumSelect
          label="Extension type"
          value={line.extension_type}
          options={ENUMS.extension_type}
          onChange={(v) => {
            const s = derivedStoreys(v);
            // Moving off mixed-storey drops the upper floor — same reasoning as the return leg
            // on the position select: dimensions for a shape the line no longer is.
            const upper = v === MIXED_STOREY_TYPE ? {} : { upper_depth_m: undefined, upper_width_m: undefined };
            onChange(s !== undefined ? { extension_type: v, storeys: s, ...upper } : { extension_type: v, ...upper });
          }}
        />
        <EnumSelect
          label="Position"
          value={line.position}
          options={ENUMS.position}
          // Moving off "wrap" drops the return leg — leaving it behind would store dimensions
          // for a shape the line no longer is, and put it back if the position ever flipped.
          onChange={(v) =>
            onChange(
              v === WRAP_POSITION
                ? { position: v }
                : { position: v, return_width_m: undefined, return_length_m: undefined },
            )
          }
        />
      </div>
      {!autoStoreys ? (
        <TextField
          label="Storeys"
          type="number"
          inputMode="numeric"
          value={line.storeys ?? ""}
          onChange={(e) => onChange({ storeys: numOrUndef(e.target.value) })}
        />
      ) : null}
      {/* A wrap-around is an L, so one depth/width pair can't describe it. These two become
          the REAR LEG and the return gets its own pair below — the labels say which rectangle
          owns the corner, because leaving that implicit is what made a wrap's floor area come
          out as a rectangle (understated by the whole return leg). */}
      <div className="lead-drawer__two">
        <TextField
          label={depthLabel}
          hint={depthHint}
          type="number" inputMode="decimal" value={line.depth_m ?? ""}
          onChange={(e) => onChange({ depth_m: numOrUndef(e.target.value) })}
        />
        <TextField
          label={widthLabel}
          hint={widthHint}
          type="number" inputMode="decimal" value={line.width_m ?? ""}
          onChange={(e) => onChange({ width_m: numOrUndef(e.target.value) })}
        />
      </div>
      {/* A mixed-storey extension is two floor plates of different sizes — the common two-storey
          rear whose first floor is shallower or narrower than the ground floor. It is ONE line
          with the upper floor measured here, never a single-storey line plus a first-floor line:
          entered as two, the engine assesses them apart and offers the ground floor a Larger
          Home Extension route a two-storey enlargement can't have (the Anand report). */}
      {isMixed ? (
        <div className="lead-drawer__two">
          <TextField
            label="Upper floor — depth (m)"
            hint="Projection from the same wall as the ground floor"
            type="number" inputMode="decimal" value={line.upper_depth_m ?? ""}
            onChange={(e) => onChange({ upper_depth_m: numOrUndef(e.target.value) })}
          />
          <TextField
            label="Upper floor — width (m)"
            hint="Usually shallower or narrower than the ground floor"
            type="number" inputMode="decimal" value={line.upper_width_m ?? ""}
            onChange={(e) => onChange({ upper_width_m: numOrUndef(e.target.value) })}
          />
        </div>
      ) : null}
      {isWrap ? (
        <div className="lead-drawer__two">
          <TextField
            label="Return — width (m)"
            hint="How far it projects beyond the side wall"
            type="number" inputMode="decimal" value={line.return_width_m ?? ""}
            onChange={(e) => onChange({ return_width_m: numOrUndef(e.target.value) })}
          />
          <TextField
            label="Return — length (m)"
            hint="Along the house, measured forward from the rear leg"
            type="number" inputMode="decimal" value={line.return_length_m ?? ""}
            onChange={(e) => onChange({ return_length_m: numOrUndef(e.target.value) })}
          />
        </div>
      ) : null}
      <div className="lead-drawer__two">
        <SelectField
          label="Height"
          hint={heightHint}
          value={line.height_m != null ? String(line.height_m) : ""}
          onChange={(e) => onChange({ height_m: e.target.value === "" ? undefined : Number(e.target.value) })}
        >
          <option value="">—</option>
          {HEIGHT_BANDS.map((b) => (
            <option key={b.value} value={String(b.value)}>{b.label}</option>
          ))}
        </SelectField>
        <SelectField
          label="Eaves height"
          hint={eavesHint}
          value={line.eaves_height_m != null ? String(line.eaves_height_m) : ""}
          onChange={(e) => onChange({ eaves_height_m: e.target.value === "" ? undefined : Number(e.target.value) })}
        >
          <option value="">—</option>
          {EAVES_BANDS.map((b) => (
            <option key={b.value} value={String(b.value)}>{b.label}</option>
          ))}
        </SelectField>
      </div>
    </div>
  );
}

/**
 * The fixed feasibility fields — the property block (asked once) + a repeating
 * list of extension lines + the budget — shared by the drawer's Project-details
 * capture and the report modal's top-up form so the pinned enum values can't
 * drift. "Add extension" appends a line; lines are removable down to one.
 */
export function BriefFields({
  budgetBand,
  property,
  extensions,
  onBudget,
  onProperty,
  onExtensions,
}: {
  budgetBand?: string;
  property: ProjectBriefProperty;
  extensions: ProjectBriefExtension[];
  onBudget: (v: string) => void;
  onProperty: (patch: Partial<ProjectBriefProperty>) => void;
  onExtensions: (next: ProjectBriefExtension[]) => void;
}): ReactNode {
  const updateLine = (i: number, patch: Partial<ProjectBriefExtension>) =>
    onExtensions(extensions.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));

  return (
    <>
      <div className="lead-drawer__two">
        <EnumSelect label="Dwelling type" value={property.dwelling_type} options={ENUMS.dwelling_type} onChange={(v) => onProperty({ dwelling_type: v })} />
        <EnumSelect label="Attachment" value={property.attachment} options={ENUMS.attachment} onChange={(v) => onProperty({ attachment: v })} />
      </div>

      {extensions.map((line, i) => (
        <ExtensionLineFields
          key={i}
          index={i}
          line={line}
          removable={extensions.length > 1}
          onChange={(patch) => updateLine(i, patch)}
          onRemove={() => onExtensions(extensions.filter((_, idx) => idx !== i))}
        />
      ))}
      <Button variant="secondary" fullWidth className="brief-ext__add" onClick={() => onExtensions([...extensions, emptyExtension()])}>
        + Add another extension
      </Button>

      <SelectField label="Budget band" value={budgetBand ?? ""} onChange={(e) => onBudget(e.target.value)}>
        <option value="">—</option>
        {ENUMS.budget_band.map((o) => (
          <option key={o} value={o}>{prettyLabel(o)}</option>
        ))}
      </SelectField>
    </>
  );
}
